import axios from 'axios';

// Auto-detect the correct API URL based on the current domain to avoid CORS issues
const getAPIUrl = () => {
  // In development, always use localhost
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000/api';
  }

  // In production, use the current domain (www.phinlex.com or phinlex.com)
  // This ensures API calls use the same origin as the frontend
  const currentOrigin = window.location.origin;
  return `${currentOrigin}/api`;
};

const API_URL = getAPIUrl();

// Force the correct API URL if it doesn't end with /api
const FINAL_API_URL = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;

// Test API connectivity
const testApiConnectivity = async () => {
  try {
    await fetch(`${FINAL_API_URL}/health`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      }
    });
  } catch (error) {
    // Silent failure
  }
};

// Run connectivity test in production
if (process.env.NODE_ENV === 'production') {
  testApiConnectivity();
}

const api = axios.create({
  baseURL: FINAL_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
  timeout: 30000,
  withCredentials: true, // send httpOnly auth cookie on every request
});

api.interceptors.request.use(
  (config) => {
    // Add cache-busting parameter for GET requests
    if (config.method === 'get') {
      const separator = config.url?.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_t=${Date.now()}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Silently handle request cancellations - this is expected behavior
    if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized globally
    if (error.response?.status === 401) {

      // Check if this is an email verification error
      const isEmailVerificationError =
        error.response?.data?.code === 'EMAIL_NOT_VERIFIED' ||
        error.response?.data?.message?.includes('Veuillez vérifier votre email') ||
        error.response?.data?.message?.includes('verify your email');

      // If it's an email verification error and we're on an auth page, don't redirect
      // Let the component handle it (it should show the verification input)
      if (isEmailVerificationError) {
        const isOnAuthPage =
          window.location.pathname.startsWith('/auth/training-center') ||
          window.location.pathname.startsWith('/auth/user') ||
          window.location.pathname.startsWith('/auth/consultant') ||
          window.location.pathname.startsWith('/auth/verify-email');

        if (isOnAuthPage) {
          // Don't redirect, let the component handle the error
          return Promise.reject(error);
        }
      }

      // Try to refresh token first if this is the first 401 attempt
      if (!error.config?.__retryCount && error.config?.url !== '/auth/refresh') {
        try {
          const refreshResponse = await api.post('/auth/refresh');
          if (refreshResponse.data?.success) {
            // Retry the original request — cookie is refreshed automatically
            error.config.__retryCount = 1;
            return api.request(error.config);
          }
        } catch (refreshError) {
          // Silent failure
        }
      }

      // Only clear auth data and redirect if we're not already on an auth page
      // and if this is not a retry attempt
      // Also don't redirect on public pages (homepage, trainings, centers, etc.)
      const isPublicPage = window.location.pathname === '/' ||
        window.location.pathname.startsWith('/trainings') ||
        window.location.pathname.startsWith('/centers') ||
        window.location.pathname.startsWith('/training/') ||
        window.location.pathname.startsWith('/training-center/');

      // Check if we are currently on an auth page
      // We use startsWith to handle sub-paths and ensure we catch all auth-related routes
      const isAuthPage =
        window.location.pathname.startsWith('/auth/training-center') ||
        window.location.pathname.startsWith('/auth/user') ||
        window.location.pathname.startsWith('/auth/consultant') ||
        window.location.pathname === '/auth/role-selection' ||
        window.location.pathname.startsWith('/auth/verify-email');

      if (!isAuthPage &&
        !isPublicPage &&
        !error.config?.__retryCount &&
        !error.config?.url?.includes('/register')) {

        localStorage.removeItem('user');

        // Use React Router navigation instead of window.location.href
        // This prevents the hard redirect that causes the logout behavior
        window.location.href = '/auth/role-selection';
      }
    }

    return Promise.reject(error);
  }
);

export default api;